import { useEffect, useRef, useState } from "react";
import { CheckIcon, CopyIcon, KeyRoundIcon, TriangleAlertIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { cn } from "~/lib/utils";

/**
 * A credential the server will never show again.
 *
 * The first one-time reveal in this app and deliberately built as a component
 * rather than inline, because M3 has three more: API key minting, the callback
 * secret rotation, and Shopify credential attachment. The rule they all share is
 * that the plaintext exists in exactly one HTTP response and nowhere else — not
 * in the database (only AES-256-GCM ciphertext), not in the audit log (the
 * redaction allowlist drops it), not in a later GET.
 *
 * So the interaction is built to be hard to dismiss by accident:
 *
 *   - modal, with no corner X and no dismissal on a backdrop click;
 *   - Escape does not close it either — `onOpenChange` refuses until the
 *     acknowledgement is ticked;
 *   - the only way out is a checkbox saying it has been saved, and then a
 *     button.
 *
 * The alternative — a toast that auto-dismisses in four seconds — loses somebody
 * a signing secret eventually, and the only recovery is a rotation that breaks
 * the merchant's live integration in the middle of whatever else is going on.
 */
export function OneTimeSecret({
  open,
  onClose,
  title,
  description,
  secret,
  /** What the value is, for the copy button's screen-reader label. */
  label = "secret",
  /** Where it has to be put, said in the caller's own words. */
  guidance,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  secret: string;
  label?: string;
  guidance?: React.ReactNode;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Reset for the next secret. Without this, minting a second one would open
  // with the acknowledgement already ticked from the first.
  useEffect(() => {
    if (open) {
      setAcknowledged(false);
      setCopied(false);
    }
  }, [open, secret]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(secret);
    } catch {
      // Denied permission, or an insecure origin. Say nothing rather than claim
      // a copy that did not happen — the value is selectable on screen, which is
      // the fallback that always works.
      return;
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  }

  function dismiss() {
    if (!acknowledged) return;
    onClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // The one place in the app where a dialog refuses to close. Escape and
        // every other implicit route out land here, and are declined until the
        // acknowledgement below is ticked.
        if (!next && !acknowledged) return;
        if (!next) onClose();
      }}
      disablePointerDismissal
    >
      <DialogContent showClose={false} className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRoundIcon className="size-4 text-warning" />
            {title}
          </DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs">
          <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0 text-warning" />
          <span>
            This is the only time this value is shown. It is stored encrypted and cannot be
            read back by anyone, through this dashboard or the API. If it is lost, the only
            way forward is to issue a new one — which stops the old one working.
          </span>
        </div>

        <div className="flex items-start gap-1">
          <code className="min-w-0 flex-1 rounded-md border bg-muted/40 p-3 font-mono text-[0.8125rem] break-all select-all">
            {secret}
          </code>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => void copy()}
            aria-label={copied ? "Copied" : `Copy ${label}`}
          >
            {copied ? (
              <CheckIcon className="size-3.5 text-success" />
            ) : (
              <CopyIcon className="size-3.5" />
            )}
          </Button>
        </div>

        {guidance ? <p className="text-xs text-muted-foreground">{guidance}</p> : null}

        <label className="flex items-start gap-2 text-sm select-none">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-primary"
          />
          <span>I have saved this somewhere safe.</span>
        </label>

        <DialogFooter>
          <Button
            size="sm"
            disabled={!acknowledged}
            onClick={dismiss}
            className={cn(!acknowledged && "cursor-not-allowed")}
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
